import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle, Check, ChevronDown, Download, ExternalLink, Eye,
  FileSignature, FileText, RefreshCw, ScrollText, X,
} from 'lucide-react';
import { packetItemRef } from '@platform/shared-types';
import type { Envelope, EnvelopePacketItem, EnvelopeSigner } from '@platform/shared-types';
import documentsApi, { type EnvelopeRow } from '../../services/documentsApi';
import { documentsExtApi } from '../../services/documentsExtApi';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
import { RubyLoader } from '../../components/RubyLoader';

/**
 * THE SIGNED APPLICATION PACKAGE, on the order that produced it.
 *
 * "Agreement signed" was a tick and a date. The paperwork behind it — the
 * device leases, the software agreement, the disclosures the customer accepted
 * — lived in the documents service and nowhere an operator on this screen
 * could reach it. So the answer to "what exactly did they sign?" was to go and
 * ask somebody, and the answer to "send me a copy" was an email thread.
 *
 * NOTHING IS FETCHED UNTIL IT IS ASKED FOR. One listing call draws the whole
 * package; a row's CONTENTS are read when that row is expanded, and a PDF is
 * presigned when that PDF is opened. Reading every envelope's detail and every
 * artifact up front cost seconds of an order screen to answer a question
 * nobody had asked yet — the operator wants ONE document, and clicks it.
 *
 * WHAT A PACKAGE ACTUALLY IS. Checkout does not create one contract. It
 * creates ONE ENVELOPE for the device+software half — every document in it
 * covered by a single signature — and one more per deal-wide line, which are
 * discharged individually (billing-service `onboarding.ts`, "packets"). So the
 * package is a handful of envelopes, each holding one or more documents, and
 * this panel shows both levels: the envelope you open and download, and the
 * documents inside it that the signature actually bound.
 *
 * ONE PDF PER ENVELOPE, NOT PER DOCUMENT. The executed artifact is the packet
 * rendered in order into a single file, and the signature's hash covers that
 * whole file — there is no per-document PDF to offer, and inventing one by
 * re-rendering a fragment would hand somebody a document that no signature
 * matches. The contents list says what is inside each file rather than
 * pretending each line is separately downloadable.
 *
 * EVERY ENVELOPE THIS TENANT HAS, not a guess at which belong to this order.
 * `order.envelopeId` names one — the primary — and the rest of the checkout's
 * packets carry no order id at all (the order does not exist yet when they are
 * created). Rather than guess at a time window and risk HIDING a signed
 * contract, the tenant's whole lane is listed and the order's own is badged.
 * The lane is a single DynamoDB partition, so this listing is complete.
 */

const prettyTitle = (templateDocumentId: string): string =>
  templateDocumentId
    .replace(/^doc_/, '')
    .split(/[-_]/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const STATUS_CHIP: Record<string, string> = {
  executed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  sent: 'bg-amber-100 text-amber-700 border-amber-200',
  partially_signed: 'bg-amber-100 text-amber-700 border-amber-200',
  draft: 'bg-stone-100 text-stone-500 border-stone-200',
  declined: 'bg-red-100 text-red-700 border-red-200',
  voided: 'bg-red-100 text-red-700 border-red-200',
  expired: 'bg-red-100 text-red-700 border-red-200',
};
const STATUS_LABEL: Record<string, string> = {
  executed: '✍ Signed',
  sent: 'Sent, unsigned',
  partially_signed: 'Partially signed',
  draft: 'Draft',
  declined: 'Declined',
  voided: 'Voided',
  expired: 'Expired',
};

const GROUP_LABEL: Record<string, string> = {
  device: 'Hardware',
  software: 'Software',
  deal: 'Deal-wide',
};

/**
 * `packetItemRef` is a RUNTIME value from the linked `@platform/shared-types`,
 * and a Vite pre-bundle built before it existed hands back `undefined` — at
 * module scope, which white-screens the page instead of failing where it is
 * used. Degrading costs the per-item accept/decline stamp; crashing costs the
 * order screen. The console line names the actual fix.
 */
const itemRef = (item: { documentId: string; versionNo: number; unitRef?: string }): string => {
  if (typeof packetItemRef === 'function') return packetItemRef(item);
  return `${item.documentId}#${item.versionNo}${item.unitRef ? `@${item.unitRef}` : ''}`;
};
if (typeof packetItemRef !== 'function') {
  console.error(
    '@platform/shared-types exported no packetItemRef — a stale Vite pre-bundle. RESTART the dev '
    + 'server: deleting node_modules/.vite under a running server does nothing, because Vite keeps '
    + 'the optimised bundle in memory and rewrites the directory.',
  );
}

const when = (iso?: string | null) =>
  (iso ? new Date(iso).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '');

/** What an expanded row has read for itself. Absent until it is expanded. */
interface Contents {
  envelope: Envelope;
  signers: EnvelopeSigner[];
}

const itemsOf = (row: EnvelopeRow, c?: Contents): EnvelopePacketItem[] => {
  const items = c?.envelope.templateDocuments;
  if (items?.length) return items;
  if (!c) return [];
  // An envelope created before packets existed is a packet of one.
  return [{
    documentId: row.templateDocumentId,
    versionNo: row.templateVersionNo,
    kind: 'agreement',
    discharge: 'signature',
    label: prettyTitle(row.templateDocumentId),
    group: 'software',
  } as EnvelopePacketItem];
};

/* ── the viewer ──────────────────────────────────────────────────────────── */

/**
 * ONE DOCUMENT, ON SCREEN — the one that was clicked and nothing else.
 *
 * The presigned URL is minted on open with `disposition=inline` and lives five
 * minutes; the frame holds the stored executed artifact, byte for byte what
 * was signed.
 *
 * IF THE FILE DOWNLOADS INSTEAD OF APPEARING, the `disposition=inline` support
 * is not on the deployed documents-ext-service yet — an older build answers
 * with `attachment`, which a frame can only hand to the browser. The panel
 * cannot detect that (the frame reports nothing), so the note under the list
 * says it plainly rather than leaving an operator staring at a blank box.
 */
const ArtifactViewer: React.FC<{
  row: EnvelopeRow;
  title: string;
  onClose: () => void;
  onDownload: () => void;
}> = ({ row, title, onClose, onDownload }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    documentsExtApi.artifactUrl(row.envelopeId, { inline: true })
      .then((r) => { if (alive) setUrl(r.url); })
      .catch((e: any) => {
        if (alive) {
          setError(String(e?.message || '').includes('404')
            ? 'This envelope has no stored PDF yet — it is not executed.'
            : (e?.message || 'The document could not be opened.'));
        }
      });
    return () => { alive = false; };
  }, [row.envelopeId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-stone-200 px-4 py-3">
          <FileText size={16} className="shrink-0 text-red-500" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-stone-900">{title}</p>
            <p className="truncate font-mono text-[10.5px] text-stone-400">{row.envelopeId}</p>
          </div>

          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden shrink-0 items-center gap-1.5 rounded-lg border border-stone-200 px-3 py-2 text-[11.5px] font-semibold text-stone-600 hover:bg-stone-50 sm:inline-flex"
            >
              <ExternalLink size={13} /> New tab
            </a>
          )}
          <button
            type="button"
            onClick={onDownload}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-[11.5px] font-semibold text-white"
            style={{ background: '#DC143C' }}
          >
            <Download size={13} /> Download
          </button>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-lg p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 bg-stone-100">
          {error && (
            <div className="flex h-full items-center justify-center px-6 text-center">
              <p className="max-w-sm text-[12.5px] text-stone-600">
                <AlertTriangle size={16} className="mx-auto mb-2 text-amber-500" />
                {error}
              </p>
            </div>
          )}
          {!error && !url && (
            <RubyLoader variant="section" className="h-full" />
          )}
          {!error && url && (
            <iframe title={title} src={url} className="h-full w-full border-0" />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── the panel ───────────────────────────────────────────────────────────── */

const AgreementPackagePanel: React.FC<{
  tenantId: string;
  /** The envelope the order itself carries — badged, and always listed first. */
  orderEnvelopeId?: string;
  orderNumber?: string;
}> = ({ tenantId, orderEnvelopeId, orderNumber }) => {
  const [rows, setRows] = useState<EnvelopeRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Read once, on first expand, and kept — expanding again is free. */
  const [contents, setContents] = useState<Record<string, Contents | 'error'>>({});
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [reading, setReading] = useState<string | null>(null);

  const [busy, setBusy] = useState<string | null>(null);
  const [bulk, setBulk] = useState(false);
  const [viewing, setViewing] = useState<EnvelopeRow | null>(null);
  const dataVersion = useDataVersion();
  /** Which tenant/envelope the spinner was last shown for — a re-read of the same one is silent. */
  const shownFor = useRef<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { envelopes } = await documentsApi.listEnvelopes({ tenantId });
      /*
        THE TENANT FILTER IS APPLIED HERE TOO, and not as a belt-and-braces
        courtesy. A documents-service that predates the `tenantId` parameter
        IGNORES it and answers with the owner-wide page — every tenant's
        envelopes — and this panel would then list another business's signed
        contracts under this order. An unknown filter that silently does not
        apply is exactly the failure the service refuses an unknown `status`
        for; the client must not depend on the deploy having happened.
      */
      const mine = (envelopes || []).filter(
        (e) => e.tenantId === tenantId || e.envelopeId === orderEnvelopeId,
      );
      // The order's own envelope leads; everything else newest first behind it.
      setRows(mine.sort((a, b) => {
        if (a.envelopeId === orderEnvelopeId) return -1;
        if (b.envelopeId === orderEnvelopeId) return 1;
        return String(b.createdAt).localeCompare(String(a.createdAt));
      }));
    } catch (e: any) {
      /* A failed BACKGROUND re-read keeps the last good rows on screen — the
         error box is only for a panel that has never loaded (see render). */
      setError(e?.message || 'The signed documents could not be loaded');
    }
  // dataVersion: re-read in place after a save anywhere on the order screen.
  }, [tenantId, orderEnvelopeId, dataVersion]);

  useEffect(() => {
    const key = `${tenantId}|${orderEnvelopeId || ''}`;
    const first = shownFor.current !== key;
    shownFor.current = key;
    // Another tenant/envelope: the rows on screen belong to the old one.
    if (first) setRows(null);
    (async () => { if (first) setLoading(true); await load(); if (first) setLoading(false); })();
  }, [load, tenantId, orderEnvelopeId]);

  /** Expanding is what asks for the contents — never the listing. */
  const toggle = useCallback(async (envelopeId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(envelopeId)) next.delete(envelopeId); else next.add(envelopeId);
      return next;
    });
    if (contents[envelopeId]) return;
    setReading(envelopeId);
    try {
      const d = await documentsApi.getEnvelope(envelopeId);
      setContents((c) => ({ ...c, [envelopeId]: { envelope: d.envelope, signers: d.signers } }));
    } catch {
      setContents((c) => ({ ...c, [envelopeId]: 'error' }));
    } finally {
      setReading(null);
    }
  }, [contents]);

  /**
   * A presigned URL handed to a hidden anchor rather than `window.open`: the
   * artifact route answers with `Content-Disposition: attachment`, so a new tab
   * would flash and close, and a popup blocker would eat the second of a run.
   */
  const saveOne = useCallback(async (envelopeId: string) => {
    const { url } = await documentsExtApi.artifactUrl(envelopeId);
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, []);

  const download = useCallback(async (envelopeId: string) => {
    setBusy(envelopeId);
    try {
      await saveOne(envelopeId);
    } catch (e: any) {
      toastService.error(String(e?.message || '').includes('404')
        ? 'That envelope has no stored PDF yet — it is not executed.'
        : (e?.message || 'Could not fetch that document'));
    } finally {
      setBusy(null);
    }
  }, [saveOne]);

  const signedRows = useMemo(
    () => (rows || []).filter((r) => r.status === 'executed'),
    [rows],
  );

  /**
   * THE WHOLE PACKAGE, and only when it is asked for. One presign per file,
   * spaced: browsers throttle a burst of downloads from one gesture and
   * silently drop the tail of it.
   */
  const downloadAll = useCallback(async () => {
    setBulk(true);
    let failed = 0;
    for (const r of signedRows) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await saveOne(r.envelopeId);
        // eslint-disable-next-line no-await-in-loop
        await new Promise((res) => { setTimeout(res, 700); });
      } catch {
        failed += 1;
      }
    }
    setBulk(false);
    if (failed) toastService.warning(`${signedRows.length - failed} of ${signedRows.length} downloaded — ${failed} could not be fetched.`);
    else toastService.success(`${signedRows.length} document${signedRows.length === 1 ? '' : 's'} downloaded.`);
  }, [signedRows, saveOne]);

  if (loading) {
    return (
      <RubyLoader variant="section" label="Loading the signed package…" />
    );
  }

  return (
    <div>
      <div className="mb-2.5 flex flex-wrap items-center gap-2">
        <h4 className="text-[11px] font-bold uppercase tracking-[0.12em] text-stone-500">
          Signed application package
        </h4>
        <span className="text-[11.5px] text-stone-400">
          {(rows || []).length} envelope{(rows || []).length === 1 ? '' : 's'}
          {signedRows.length ? ` · ${signedRows.length} signed` : ''}
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => { void load(); }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-2.5 py-1.5 text-[11px] font-semibold text-stone-600 hover:bg-stone-50"
          >
            <RefreshCw size={12} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => { void downloadAll(); }}
            disabled={bulk || signedRows.length === 0}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[11px] font-semibold text-white disabled:opacity-40"
            style={{ background: '#DC143C' }}
          >
            {bulk ? <RubyGemMark size={12} drawing color="currentColor" /> : <Download size={12} />}
            Download whole package
          </button>
        </div>
      </div>

      {error && rows === null && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11.5px] text-red-700">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" /> {error}
        </div>
      )}

      {rows && rows.length === 0 && (
        <p className="py-2 text-[12px] text-stone-500">
          Nothing has been sent for signature on this account yet.
        </p>
      )}

      <div className="space-y-2">
        {(rows || []).map((row) => {
          const c = contents[row.envelopeId];
          const detail = c === 'error' ? undefined : c;
          const items = itemsOf(row, detail);
          const decisions = (detail?.envelope.itemDecisions ?? {}) as Record<
            string, { decision: string; at: string } | undefined>;
          const isOpen = expanded.has(row.envelopeId);
          const isOrders = row.envelopeId === orderEnvelopeId;
          const signed = row.status === 'executed';
          return (
            <div
              key={row.envelopeId}
              className={`rounded-xl border bg-white ${
                isOrders ? 'border-emerald-200 shadow-[0_2px_12px_rgba(16,185,129,.08)]' : 'border-stone-200'}`}
            >
              <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                {/* The row itself opens the contents. Nothing is read until it does. */}
                <button
                  type="button"
                  onClick={() => { void toggle(row.envelopeId); }}
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg ${
                    signed ? 'bg-emerald-50 text-emerald-600' : 'bg-stone-100 text-stone-400'}`}
                  >
                    {signed ? <Check size={15} strokeWidth={3} /> : <FileSignature size={15} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[12.5px] font-semibold text-stone-900">
                      {prettyTitle(row.templateDocumentId)}
                      {isOrders && (
                        <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-emerald-700">
                          This order{orderNumber ? ` · ${orderNumber}` : ''}
                        </span>
                      )}
                    </span>
                    <span className="mt-0.5 block truncate font-mono text-[10.5px] text-stone-400">
                      {row.envelopeId}
                      {row.executedAt ? ` · signed ${when(row.executedAt)}` : ''}
                      {!row.executedAt && row.sentAt ? ` · sent ${when(row.sentAt)}` : ''}
                    </span>
                  </span>
                  {reading === row.envelopeId
                    ? <RubyGemMark size={13} drawing color="currentColor" />
                    : (
                      <ChevronDown
                        size={14}
                        className={`shrink-0 text-stone-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      />
                    )}
                </button>

                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                  STATUS_CHIP[row.status] || 'border-stone-200 bg-stone-100 text-stone-500'}`}
                >
                  {STATUS_LABEL[row.status] || row.status}
                </span>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setViewing(row)}
                    disabled={!signed}
                    title={signed ? 'Open this signed PDF' : 'There is no signed PDF until it is executed'}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-2.5 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
                  >
                    <Eye size={12} /> View
                  </button>
                  <button
                    type="button"
                    onClick={() => { void download(row.envelopeId); }}
                    disabled={!signed || busy === row.envelopeId}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 px-2.5 py-1.5 text-[11px] font-semibold text-stone-700 hover:bg-stone-50 disabled:opacity-40"
                  >
                    {busy === row.envelopeId
                      ? <RubyGemMark size={12} drawing color="currentColor" />
                      : <Download size={12} />}
                    Download
                  </button>
                </div>
              </div>

              {/* WHAT ONE SIGNATURE BOUND — read on expand, not before. */}
              {isOpen && (
                <div className="border-t border-stone-100 px-3 py-2">
                  {c === 'error' && (
                    <p className="text-[11px] text-amber-700">
                      The contents of this envelope could not be read just now — the file itself
                      still opens and downloads.
                    </p>
                  )}
                  {!c && reading !== row.envelopeId && (
                    <p className="text-[11px] text-stone-400">Nothing read yet.</p>
                  )}
                  {items.length > 0 && (
                    <ul className="space-y-1">
                      {items.map((it) => {
                        const decision = decisions[itemRef(it)];
                        const policy = it.discharge === 'accept_decline';
                        return (
                          <li key={itemRef(it)} className="flex items-center gap-2">
                            {policy
                              ? <ScrollText size={12} className="shrink-0 text-stone-400" />
                              : <FileText size={12} className="shrink-0 text-stone-400" />}
                            <span className="min-w-0 flex-1 truncate text-[11.5px] text-stone-700">{it.label}</span>
                            <span className="shrink-0 rounded bg-stone-100 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-stone-500">
                              {GROUP_LABEL[it.group] || it.group}
                            </span>
                            <span className={`shrink-0 text-[10.5px] ${
                              decision?.decision === 'declined' ? 'text-red-600' : 'text-stone-400'}`}
                            >
                              {policy
                                ? (decision ? `${decision.decision} ${when(decision.at)}` : 'awaiting answer')
                                : (signed ? 'signed' : 'awaiting signature')}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {detail?.signers?.length ? (
                    <p className="mt-1.5 text-[10.5px] text-stone-400">
                      {detail.signers.map((s) => `${s.name}${s.signedAt ? ` · signed ${when(s.signedAt)}` : ` · ${s.status}`}`).join('  ·  ')}
                    </p>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] text-stone-500">
        Each file is the stored executed PDF — the exact document that was signed, never a
        re-render — and one file covers every document listed inside it. Links are minted per
        click and expire in minutes.
      </p>

      {viewing && (
        <ArtifactViewer
          row={viewing}
          title={prettyTitle(viewing.templateDocumentId)}
          onClose={() => setViewing(null)}
          onDownload={() => { void download(viewing.envelopeId); }}
        />
      )}
    </div>
  );
};

export default AgreementPackagePanel;
